import {
  getCrewBaseAtDate,
  IBidSettingsValue,
  ICrewEventLedger,
  ICrewLogicModifier,
  ICrewsNonFlyingAssignment,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IDisplayPairing,
  IFullPairingPosition,
  INonFlyingAssignment,
  INonFlyingPairingAssignment,
  IPairing,
  IPairingPosition,
  IQualificationCheckResult,
  IRunningTotalsResult,
  ISearchPairing,
  isIPairingPosition,
  IUser,
  IViolationCheckStatus,
  IViolationResult,
  unassignedPairingId,
} from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import * as CrewScheduleActions from "../actions/CrewSchedule/CrewSchedule.Actions";
import { IAirportOption } from "./StructuresReducer";

export interface ICrewMember extends IUser {
  isSelected: boolean;
  isExpanded: boolean; // Boolean to determine if this crew row is expanded.
  lastModified: number;
  violationCheckStatus?: IViolationCheckStatus;
}

interface ICrewPositionIndex {
  positionIndex: number;
}

export interface IBaseTime {
  id?: any;
  label?: any;
  isUTC: boolean;
  timezoneId?: string;
}

export interface ICrewTimeOption {
  label: string;
  value: IBaseTime;
  key: string;
}

interface ICrewMemberPlannedEvents {
  plannedEvents: CrewSchedEvent[][];
  lastModified: number;
}
export interface ICrewSchedEventMap {
  [key: string]: ICrewMemberPlannedEvents;
}

export type CrewSchedEvent = IDisplayPairing | ICrewsNonFlyingAssignment | null;
export type CrewPairingPositionGrouped = ICrewFullPairingPosition & ICrewPositionIndex;
export type ICrewPairingPosition = ICrewMember | IPairingPosition;
export type ICrewFullPairingPosition = ICrewMember | IFullPairingPosition;
export interface ICrewPairingPositionMap {
  [position: string]: CrewPairingPositionGrouped[];
}

// export interface IPairingPosition {
//   crew_id: string;
//   positon: string;
// }

export interface ICrewViolations {
  [yearMonth: string]: {
    regulationResults: IViolationResult[];
    runningTotalResults: IRunningTotalsResult[];
    lastModified?: number; //the time that this made into a violation map on the frontend
    createdAt?: number; // violations were calculated based on the crew's schedule at this time
  };
}

export interface ICrewViolationsMap {
  [crewId: string]: {
    createdAt: number; // violations were calculated based on the crew's schedule at this time
    violations: ICrewViolations;
  };
}

export interface IAnchoredHoveredEvents {
  hoveredEvents: IHoveredEvent[];
  anchorPos: {
    x: number;
    y: number;
  };
  windowId: string | null; // Added so that we know crew sched window (if more than one open)
}

export interface IAnchoredHoveredViolation {
  hoveredViolation: IViolationResult | null;
  anchorPos: {
    x: number;
    y: number;
  };
}

export interface ICrewPairing {
  lastModified: number;
  pairings: IDisplayPairing[];
}

export interface ICrewPairingMap {
  [crewId: string]: ICrewPairing;
}

export interface ICrewBidPairing {
  lastModified: number;
  bidPairings: IDisplayBidPairing[];
}

export interface ICrewBidPairingMap {
  [crewId: string]: ICrewBidPairing;
}

export interface ICrewBidNonFlyingAssignment {
  lastModified: number;
  bidNFAs: IDisplayBidNFA[];
}

export interface ICrewBidNonFlyingAssignmentMap {
  [crewId: string]: ICrewBidNonFlyingAssignment;
}

export interface ICrewNonFlyingAssignment {
  lastModified: number;
  nonFlyingAssignments: ICrewsNonFlyingAssignment[];
}

export interface ICrewNonFlyingAssignmentMap {
  [crewId: string]: ICrewNonFlyingAssignment;
}

export interface ICrewScheduleEventMap {
  [crewId: string]: ICrewScheduleEvent;
}

export interface IFocusCrewSchedArea {
  timeInterval: number[];
  crewIds: string[];
}

export interface ITimeIntervalLoaded {
  [yearMonth: string]: {
    startOfInterval: number;
    endOfInterval: number;
  };
}

export interface ITemporaryPairing {
  start: number;
  end: number;
  crewId: string;
  pairingId: any;
  shortId?: string;
  positions?: IPairingPosition[];
  assignPos?: string;
}

export interface IDayLogicModifierMap {
  [monthDay: string]: ICrewLogicModifier[];
}
export interface ICrewLogicModifierMap {
  [crewId: string]: IDayLogicModifierMap;
}

export interface IQualificationMap {
  [qualId: string]: IQualificationCheckResult[];
}
export interface ICrewQualifications {
  [crewId: string]: {
    qualifications: IQualificationMap;
    createdAt: number; // quals were calculated at this time
  };
}

interface ICrewScheduleWindowProperties {
  [key: string]: {
    displayTimezone: IAirportOption;
    crewBase: ICrewTimeOption;
    focusCrewSchedArea: IFocusCrewSchedArea;
    hideTimeBetweenEvents: boolean;
    hideCheckInCheckOutTimes: boolean;
    openFlying: {
      requestIDs: any[];
      pairingID: string;
    } | null;
  };
}

export interface ICrewEventLedgerMap {
  [crewId: string]: ICrewEventLedger[];
}

export interface ICrewSchedule {
  windowProperties: ICrewScheduleWindowProperties;
  crewMembers: ICrewMember[];
  filteredCrew: { [key: string]: ICrewMember[] };
  scrollTop: number;
  selectedCrewEvents: ICrewEvent[];
  crewNonFlyingAssignments: ICrewNonFlyingAssignmentMap;
  crewPairings: ICrewPairingMap;
  crewViolations: ICrewViolationsMap | null;
  crewPlannedEvents: ICrewSchedEventMap;
  crewDayLogicModifiers: ICrewLogicModifierMap;
  focusCrewSchedArea: IFocusCrewSchedArea | null;
  focusCrewSchedPairing: string | null;
  crewDataLoading: string[];
  timeIntervalLoaded: ITimeIntervalLoaded;
  crewDataLoaded: boolean;
  temporaryPairings: ITemporaryPairing[];
  selectedCrewDays?: ISelectedCrewDays;
  hoveredEvents?: IAnchoredHoveredEvents;
  displayTimezone: IAirportOption;
  groupFilter?: { [key: string]: string[] };
  searchCrewMember: { [key: string]: string[] };
  crewQualifications: ICrewQualifications | null;
  hoveredViolation: IAnchoredHoveredViolation;
  hideDropOffHours: boolean;
  crewLedgers: ICrewEventLedgerMap;
  isLoadingCrewViolations: boolean;
  loadedViolations: boolean;
  isLoadingCrewQualifications: boolean;
  pairingEditors: { [key: string]: IPairing };
  waterfallWindow: {
    pairings: IDisplayPairing[];
  };
  clearViolationsTimeoutId: NodeJS.Timeout | undefined;
  violationCheckStatus: { [crewId: string]: IViolationCheckStatus };
  timelineLink: {
    start: number;
    end: number;
    activeWindowId: string;
  } | null;
  lastOpenFlyingAssignment: {
    assignedAt: Date | null;
    requestResponse: any | null;
  } | null;
  biddingSettings: IBidSettingsValue;
}

export const defaultCrewBase: ICrewTimeOption = {
  value: {
    isUTC: true,
    timezoneId: "Etc/UTC",
  },
  key: "All",
  label: "ALL",
};

//TODO Replace this with a simpler timezone object once we have an endpoint to retrieve timezone options.instead of relying on airport info.
export const defaultTimezone: IAirportOption = {
  _id: undefined,
  ident: "All",
  name: "UTC",
  timezoneId: "Etc/UTC", // "Universal Coordinated Time",
  iata: null,
};

export const initialState: ICrewSchedule = {
  windowProperties: {},
  crewMembers: [],
  filteredCrew: {},
  crewNonFlyingAssignments: {},
  crewPairings: {},
  crewViolations: null,
  crewPlannedEvents: {},
  crewDayLogicModifiers: {},
  scrollTop: 0,
  selectedCrewEvents: [],
  selectedCrewDays: { crewId: "", selectedTimes: [], timezone: "Etc/UTC" },
  focusCrewSchedArea: null,
  focusCrewSchedPairing: null,
  timeIntervalLoaded: {},
  crewDataLoading: [],
  crewDataLoaded: false,
  temporaryPairings: [],
  hoveredEvents: {
    anchorPos: {
      x: -1,
      y: -1,
    },
    hoveredEvents: [],
    windowId: null,
  },
  hoveredViolation: {
    anchorPos: {
      x: -1,
      y: -1,
    },
    hoveredViolation: null,
  },
  displayTimezone: defaultTimezone,
  groupFilter: {},
  searchCrewMember: {},
  crewQualifications: null,
  hideDropOffHours: true,
  crewLedgers: {},
  isLoadingCrewViolations: false,
  loadedViolations: false,
  isLoadingCrewQualifications: false,
  pairingEditors: {},
  waterfallWindow: {
    pairings: [],
  },
  clearViolationsTimeoutId: undefined,
  violationCheckStatus: {},
  timelineLink: null,
  lastOpenFlyingAssignment: {
    assignedAt: null,
    requestResponse: null,
  },
  biddingSettings: { enabled: false, ranks: [{ prefRank: 0, label: "", color: "" }], displayPlannedCredit: false },
};

export const CrewScheduleReducer = (
  state: ICrewSchedule = initialState,
  action: CrewScheduleActions.Action
): ICrewSchedule => {
  switch (action.type) {
    case CrewScheduleActions.Type.SET_BIDDING_SETTINGS:
      return { ...state, biddingSettings: action.biddingSettings };
    case CrewScheduleActions.Type.SET_WINDOW_TIMEZONE:
      //If timezone contains any white space i.e "Universal Coordinated Time", change timezoneId to Etc/UTC standard.
      const airportOption: IAirportOption = action?.displayTimezone;
      const correctTimezone = /.*\s.*/g.test(airportOption?.timezoneId)
        ? { ...airportOption, timezoneId: "Etc/UTC" }
        : airportOption;

      return {
        ...state,
        windowProperties: {
          ...state.windowProperties,
          [action.windowId]: { ...state.windowProperties[action.windowId], displayTimezone: correctTimezone },
        },
      };
    case CrewScheduleActions.Type.SET_ACTIVE_OPEN_FLYING_REQUEST:
      const updatedOpenFlying =
        !!action?.requestIDs?.length && action?.pairingID
          ? {
              requestIDs: action.requestIDs,
              pairingID: action.pairingID,
            }
          : null;
      return {
        ...state,
        windowProperties: {
          ...state.windowProperties,
          [action.windowId]: {
            ...state.windowProperties[action.windowId],
            openFlying: updatedOpenFlying,
          },
        },
      };
    case CrewScheduleActions.Type.SET_CREW_BASE:
      // return { ...state, crewBase: { ...state.crewBase, [action.windowId]: action.crewBase } };
      return {
        ...state,
        windowProperties: {
          ...state.windowProperties,
          [action.windowId]: { ...state.windowProperties[action.windowId], crewBase: action.crewBase },
        },
      };
    case CrewScheduleActions.Type.TIMELINE_LINK:
      const updatedLink = action.activeWindowId
        ? {
            activeWindowId: action.activeWindowId,
            start: action.start,
            end: action.end,
          }
        : null;
      return { ...state, timelineLink: updatedLink };
    case CrewScheduleActions.Type.SET_LAST_OPEN_FLYING_ASSIGNMENT_RESPONSE:
      const { assignedAt, requestResponse } = action;
      return {
        ...state,
        lastOpenFlyingAssignment: {
          assignedAt,
          requestResponse,
        },
      };
    case CrewScheduleActions.Type.UPDATE_CREWMEMBER:
      const updateCrewMembers = [...state.crewMembers];
      const crewIndex = updateCrewMembers.findIndex((crew) => crew._id === action.crew._id);
      if (action.mode === "create") {
        // Add crew member
        const newCrew = {
          isSelected: false,
          isExpanded: false,
          lastModified: Date.now(),
          ...action.crew,
          operatingBase: getCrewBaseAtDate(action.crew.crewInfo.bases, new Date()?.valueOf()),
        };
        updateCrewMembers.push(newCrew);
      } else if (crewIndex > -1 && action.mode === "update") {
        // update crew member
        updateCrewMembers[crewIndex] = {
          ...updateCrewMembers[crewIndex],
          ...action.crew,
          operatingBase: getCrewBaseAtDate(action.crew.crewInfo.bases, new Date()?.valueOf()),
        };
      } else if (crewIndex > -1 && action.mode === "delete") {
        // remove crew member
        updateCrewMembers.splice(crewIndex, 1);
      }
      return { ...state, crewMembers: updateCrewMembers };
    case CrewScheduleActions.Type.GOT_CREW:
      return { ...state, crewMembers: action.crew };
    case CrewScheduleActions.Type.FILTERED_CREW:
      return { ...state, filteredCrew: { ...state.filteredCrew, [action.windowId]: action.filtered } };
    case CrewScheduleActions.Type.REMOVE_FILTERED_CREW:
      const updatedFilteredCrew = { ...state.filteredCrew };
      delete updatedFilteredCrew[action.windowId];
      return { ...state, filteredCrew: { ...updatedFilteredCrew } };
    case CrewScheduleActions.Type.GOT_PAIRINGS:
      return {
        ...state,
        // pairings: Array.from(new Set([...[...state.pairings, ...action.pairings]])),
        crewPairings: setCrewPairings(action.pairings, state),
      };
    case CrewScheduleActions.Type.GOT_NONFLYING_ASSIGNMENTS:
      return {
        ...state,
        crewNonFlyingAssignments: setCrewNonFlyingAssignments(action.nonFlyingAssignments, state),
      };
    case CrewScheduleActions.Type.UPDATE_CREW_PAIRINGS:
      return {
        ...state,
        crewPairings: { ...action.crewPairingMap },
      };
    case CrewScheduleActions.Type.UPDATE_CREW_NFAS:
      return {
        ...state,
        crewNonFlyingAssignments: action.crewNFAMap,
      };
    case CrewScheduleActions.Type.UPDATE_SEARCH_CREW_MEMBER:
      return {
        ...state,
        searchCrewMember: {
          ...state.searchCrewMember,
          [action.windowId]: action.searchedCrewMembers,
        },
      };
    case CrewScheduleActions.Type.TOGGLE_SELECTED_CREW_MEMBER:
      // New Single select logic.
      const crewMembers = [...state.crewMembers];
      crewMembers.forEach((crew) => {
        if (action.id === crew._id && !crew.isExpanded) {
          // If force flag is set, force true (used for context menu which doesn't toggle state);
          const newState = action.forceTrue ? true : !crew.isSelected;
          crew.isSelected = newState;
          crew.lastModified = Date.now();
          if (action.forceExpand) crew.isExpanded = newState;
        } else {
          crew.isSelected = false;
          if (action.forceExpand) crew.isExpanded = false;
        }
      });
      // Dertermine whether the selected days objects needs to be set null.
      const selectedCrewDays =
        action.id === state.selectedCrewDays?.crewId && action.forceTrue ? state.selectedCrewDays : undefined;
      return { ...state, crewMembers, selectedCrewDays };
    case CrewScheduleActions.Type.TOGGLE_EXPANDED_CREW_ROW:
      const crewRows = [...state.crewMembers];
      const isExpanded = (crew: ICrewMember): boolean => crew._id === action.id;
      const indexOfRow: number = crewRows.findIndex(isExpanded);
      crewRows[indexOfRow].isExpanded = !crewRows[indexOfRow].isExpanded;
      crewRows[indexOfRow].lastModified = Date.now();
      return { ...state, crewMembers: crewRows };
    case CrewScheduleActions.Type.EXPAND_COLLAPSE_ALL_CREW_ROWS:
      const allRows = [...state.crewMembers];
      const lastModified = Date.now();
      allRows.map((crew) => {
        crew.isExpanded = action.state;
        crew.lastModified = lastModified;
        return crew;
      });
      return { ...state, crewMembers: allRows };
    case CrewScheduleActions.Type.DESELECT_COLLAPSE_ALL_ROWS:
      const deselectCollapseAllRows = [...state.crewMembers];
      deselectCollapseAllRows.map((crew) => {
        crew.isExpanded = false;
        crew.isSelected = false;
        crew.lastModified = Date.now();
        return crew;
      });
      return { ...state, crewMembers: deselectCollapseAllRows };
    case CrewScheduleActions.Type.SET_SCROLL_TOP:
      return { ...state, scrollTop: state.scrollTop + action.scrollAmount };
    case CrewScheduleActions.Type.SET_SELECTED_CREW_EVENTS:
      // Create array of all unique crew ids found in newly selected crew events.
      const crewIds = Array.from(new Set([...action.newEvents.map((event) => event.crewId)]));

      const selectedCrewEvents = updateSelectedCrewEvents(
        state.crewPairings,
        state.crewNonFlyingAssignments,
        crewIds,
        state.selectedCrewEvents,
        action.newEvents,
        action.shiftKey,
        action.ctrlKey,
        action.update
      );
      const focusedArea: IFocusCrewSchedArea | null = action.shouldFocus ? { timeInterval: [], crewIds: [] } : null;
      if (focusedArea && action.newEvents.length > 0) {
        action.newEvents.forEach((selected: IHoveredEvent) => {
          focusedArea.crewIds.push(selected.crewId);
          if (focusedArea !== null && focusedArea.timeInterval.length === 0) {
            focusedArea.timeInterval = [selected?.event?.start?.valueOf(), selected?.event?.end?.valueOf()];
          } else {
            if ((selected?.event?.start as number) < focusedArea.timeInterval[0]) {
              focusedArea.timeInterval[0] = selected?.event?.start?.valueOf();
            }
            if ((selected?.event?.end as number) > focusedArea.timeInterval[1]) {
              focusedArea.timeInterval[1] = selected?.event?.end?.valueOf();
            }
          }
        });
      }

      const curMembers = [...state.crewMembers];
      //deselct any selected crew that aren't included in the selected events if there are any selected events
      curMembers.forEach((crew) => {
        if (crewIds?.length > 0 && !crewIds.includes(crew._id)) {
          crew.isSelected = false;
          crew.isExpanded = false;
        }
        crew.lastModified = Date.now();
      });
      return {
        ...state,
        selectedCrewEvents,
        selectedCrewDays: undefined,
        crewMembers: curMembers,
        windowProperties: setWindowProperty("focusCrewSchedArea", focusedArea, { ...state.windowProperties }),
      };
    case CrewScheduleActions.Type.SET_SELECTED_CREW_DAYS:
      // Select crew member with selected days (deselect all others).
      const members = [...state.crewMembers];
      members.forEach((crew) => {
        if (action?.selectedCrewDays?.crewId === crew._id) {
          crew.isSelected = true;
        } else {
          crew.isSelected = false;
        }
        crew.lastModified = Date.now();
      });
      return {
        ...state,
        selectedCrewDays: action.selectedCrewDays,
        selectedCrewEvents: [],
        crewMembers: members,
      };
    case CrewScheduleActions.Type.SET_SELECTED_CREW_DAY_MEMBER:
      return {
        ...state,
        selectedCrewDays: {
          ...state.selectedCrewDays,
          crewId: action.newCrewId,
        },
        selectedCrewEvents: [],
      };
    case CrewScheduleActions.Type.SET_SELECTED_CREW_DAY_TIME:
      return {
        ...state,
        selectedCrewDays: {
          ...state.selectedCrewDays,
          selectedTimes: [action.selectedTime],
        },
        selectedCrewEvents: [],
      };
    case CrewScheduleActions.Type.GOT_VIOLATIONS:
      //check the createdAt time - don't love how I get the createdAt time
      const prevCreatedAtDate = state.crewViolations?.[action.crewId]
        ? state.crewViolations[action.crewId]?.createdAt
        : 0;
      if (prevCreatedAtDate < action.createdAt) {
        //do nothing, what is in redux is older than what is coming in...
        return {
          ...state,
          crewViolations: {
            ...state.crewViolations,
            [action.crewId]: {
              createdAt: action.createdAt,
              violations: action.crewMap,
            },
          },
        };
      } else {
        return { ...state };
      }
    case CrewScheduleActions.Type.IS_LOADING_CREW_VIOLATIONS:
      return {
        ...state,
        isLoadingCrewViolations: action.isLoading,
      };
    case CrewScheduleActions.Type.LOADED_INITIAL_CREW_VIOLATIONS:
      return {
        ...state,
        loadedViolations: action.loaded,
      };
    case CrewScheduleActions.Type.CLEAR_CREW_VIOLATIONS:
      //just in case, add a check for crewId
      if (!action.crewId) {
        return { ...state };
      }
      return {
        ...state,
        crewViolations: {
          ...state.crewViolations,
          [action.crewId]: {
            createdAt: null,
            violations: null,
          },
        },
      };
    case CrewScheduleActions.Type.CLEAR_ALL_VIOLATIONS:
      return {
        ...state,
        crewViolations: null,
      };
    case CrewScheduleActions.Type.SET_CLEAR_VIOLATIONS_TIMEOUT_ID:
      //clear the current timer using the timeoutId saved in redux
      if (state.clearViolationsTimeoutId) {
        clearTimeout(state.clearViolationsTimeoutId);
      }
      return {
        ...state,
        clearViolationsTimeoutId: action.timeoutId,
      };
    case CrewScheduleActions.Type.SET_HOVERED_VIOLATION:
      return {
        ...state,
        hoveredViolation: action.hoveredViolation,
      };
    case CrewScheduleActions.Type.GOT_VIOLATION_CHECK_STATUS: {
      if (action.crewToUpdate) {
        return {
          ...state,
          violationCheckStatus: { ...state.violationCheckStatus, ...action.crewToUpdate },
        };
      } else {
        return state;
      }
    }
    case CrewScheduleActions.Type.GOT_DAY_LOGIC_MODIFIERS:
      return {
        ...state,
        crewDayLogicModifiers: updateCrewDayLogicModifiers(state.crewDayLogicModifiers, action.dayLogicModifiers),
      };
    case CrewScheduleActions.Type.SET_DAY_LOGIC_MODIFIERS:
      return {
        ...state,
        crewDayLogicModifiers: action.crewDayLogicModifiers,
      };
    case CrewScheduleActions.Type.LOADED_DATA_INTERVAL:
      return {
        ...state,
        timeIntervalLoaded: { ...state.timeIntervalLoaded, ...action.interval },
      };
    case CrewScheduleActions.Type.TIMELINE_CREW_DATA_LOADING:
      const currentInterval = [...state.crewDataLoading];
      if (action.loaded) {
        const index = currentInterval.indexOf(action.interval);
        if (index > -1) {
          currentInterval.splice(index, 1);
        }
      } else {
        currentInterval.push(action.interval);
      }
      return { ...state, crewDataLoading: currentInterval };
    case CrewScheduleActions.Type.IS_LOADING:
      return { ...state, crewDataLoaded: action.loading };
    case CrewScheduleActions.Type.ADD_TEMPORARY_PAIRING:
      return {
        ...state,
        temporaryPairings: [...state.temporaryPairings, action.temporaryPairing],
      };
    case CrewScheduleActions.Type.UPDATE_TEMPORARY_PAIRING:
      const updatedPairing = state.temporaryPairings.map((temp: ITemporaryPairing) => {
        if (temp.pairingId === action.temporaryPairing.pairingId.toString()) {
          return {
            start: action.temporaryPairing.start,
            end: action.temporaryPairing.end,
            crewId: temp.crewId,
            pairingId: temp.pairingId,
          };
        } else return temp;
      });
      return {
        ...state,
        temporaryPairings: updatedPairing,
      };
    case CrewScheduleActions.Type.REMOVE_TEMPORARY_PAIRING:
      //remove any tempPairings with the pairing id
      const updateDatedTempPairings = state.temporaryPairings.filter((temps: ITemporaryPairing) => {
        //case where you only want to remove a temp pairing from a specfic id
        if (action.crewId) {
          return !(temps.pairingId === action.pairingId.toString() && temps.crewId === action.crewId.toString());
        } else return temps.pairingId !== action.pairingId.toString();
      });
      return {
        ...state,
        temporaryPairings: updateDatedTempPairings,
      };

    case CrewScheduleActions.Type.GOT_PLANNED_EVENTS:
      const newCrewPlannedEventmap = updateCrewPlannedEventMap(state.crewPlannedEvents, action.plannedEvents);
      return {
        ...state,
        crewPlannedEvents: newCrewPlannedEventmap,
      };
    case CrewScheduleActions.Type.UPDATE_PLANNED_EVENTS:
      return {
        ...state,
        crewPlannedEvents: action.crewPlannedEvents,
      };
    case CrewScheduleActions.Type.SET_HOVERED_EVENTS:
      return {
        ...state,
        hoveredEvents: action.hoveredEvents,
      };
    case CrewScheduleActions.Type.SET_FOCUSED_AREA:
      return {
        ...state,
        windowProperties: setWindowProperty(
          "focusCrewSchedArea",
          action.focusedArea,
          { ...state.windowProperties },
          action.windowId
        ),
      };
    case CrewScheduleActions.Type.SET_FOCUSED_PAIRING:
      return {
        ...state,
        focusCrewSchedPairing: action.focusedPairing,
      };
    case CrewScheduleActions.Type.SET_CREW_GROUP_FILTER:
      return { ...state, groupFilter: { ...state.groupFilter, [action.windowId]: action.groups } };
    case CrewScheduleActions.Type.UPDATE_CREW_QUALIFICATIONS:
      return {
        ...state,
        crewQualifications: updateCrewQualifications(state.crewQualifications, action.qualifications, action.crewIds),
        isLoadingCrewQualifications: false,
      };
    case CrewScheduleActions.Type.IS_LOADING_CREW_QUALIFICATIONS:
      return {
        ...state,
        isLoadingCrewQualifications: action.isLoading,
      };
    case CrewScheduleActions.Type.SET_HIDE_DROPOFF_HOURS:
      return { ...state, hideDropOffHours: action.hideDropOffHours };
    case CrewScheduleActions.Type.SET_HIDE_CHECKIN_CHECKOUT_TIMES:
      return {
        ...state,
        windowProperties: setWindowProperty(
          "hideCheckInCheckOutTimes",
          action.hideCheckInCheckOutTimes,
          { ...state.windowProperties },
          action.windowId
        ),
      };
    case CrewScheduleActions.Type.SET_HIDE_TIME_BETWEEN_EVENTS:
      return {
        ...state,
        windowProperties: setWindowProperty(
          "hideTimeBetweenEvents",
          action.hideTimeBetweenEvents,
          { ...state.windowProperties },
          action.windowId
        ),
      };
    // case CrewScheduleActions.Type.UPDATE_CREW_VIOLATIONS:
    //   return {
    //     ...state,
    //     crewViolations: updateCrewViolations(state.crewViolations, action.violations),
    //   };
    case CrewScheduleActions.Type.GOT_CREW_MEMBER_LEDGER:
      // const currentEntries = state.crewLedgers[action.crewId] ?? [];
      // const updatedEntries: ICrewEventLedger[] = Array.from(new Set([...currentEntries, ...action.crewLedgers]));
      return {
        ...state,
        crewLedgers: { ...state.crewLedgers, [action.crewId]: action.crewLedgers },
      };
    case CrewScheduleActions.Type.ADD_PAIRING_EDITOR:
      return {
        ...state,
        pairingEditors: {
          ...state.pairingEditors,
          [action.pairing._id]: action.pairing,
        },
      };
    case CrewScheduleActions.Type.UPDATE_PAIRING_EDITOR:
      return {
        ...state,
        pairingEditors: {
          ...state.pairingEditors,
          [action.pairingId]: { ...state.pairingEditors[action.pairingId], ...action.updates },
        },
      };
    case CrewScheduleActions.Type.REMOVE_PAIRING_EDITOR:
      const tempState = {
        ...state.pairingEditors,
      };
      delete tempState[action.pairingId];
      return {
        ...state,
        pairingEditors: tempState,
      };
    case CrewScheduleActions.Type.REMOVE_WINDOW_PROPERTIES:
      const updatedWindowProperties = { ...state.windowProperties };
      delete updatedWindowProperties[action.windowId];
      return { ...state, windowProperties: { ...updatedWindowProperties } };
    case CrewScheduleActions.Type.UPDATE_WATERFALL_PAIRINGS:
      return { ...state, waterfallWindow: { ...state.waterfallWindow, pairings: action.pairings ?? [] } };
    default:
      return state;
  }
};

export const newCrewPairing = (lastModified: number): ICrewPairing => {
  return {
    lastModified,
    pairings: [],
  };
};

export const newCrewNFA = (lastModified: number): ICrewNonFlyingAssignment => {
  return {
    lastModified,
    nonFlyingAssignments: [],
  };
};

/**
 * updateCrewDayLogicModifiers - updates the original CLM Map with the new dayLogicModifiers.
 * Inserts the dayLogicModifiers in the right place in the maps as well as right place in array
 * @param original - The original ICrewLogicModifierMap to update
 * @param dayLogicModifiers - The new ICrewLogicModifier[] to insert into the new crewLogicModifier map
 */
export const updateCrewDayLogicModifiers = (
  original: ICrewLogicModifierMap,
  dayLogicModifiers: ICrewLogicModifier[]
): ICrewLogicModifierMap => {
  const crewLogicModifierMap = { ...original };
  dayLogicModifiers.forEach((dayLogicModifier: ICrewLogicModifier) => {
    const crewId = dayLogicModifier.crew_id;
    // Clone the per-crew day map (and the day array below) so we build new
    // references instead of mutating the previous state in place.
    crewLogicModifierMap[crewId] = { ...(crewLogicModifierMap[crewId] ?? {}) };
    const dayLM = new Date(dayLogicModifier.date);

    // You have to add 1 to the month here as javascript returns jan as 0 and not as 1.
    const dayMonthStr = `${dayLM.getUTCFullYear()}-${dayLM.getUTCMonth() + 1}-${dayLM.getUTCDate()}`;

    // Drop any copy of this modifier already on the day, so receiving it twice (from overlapping
    // fetch chunks, or a websocket update racing a fetch) replaces it instead of displaying it twice.
    const dayCrewLogicModifiers = (crewLogicModifierMap[crewId][dayMonthStr] ?? []).filter(
      (clm: ICrewLogicModifier) => clm._id !== dayLogicModifier._id
    );
    const index = findIndexToInsertLogicModifier(
      dayCrewLogicModifiers,
      dayLogicModifier,
      0,
      dayCrewLogicModifiers.length - 1
    );
    dayCrewLogicModifiers.splice(index, 0, dayLogicModifier);
    //IDK if we need to sort the CLM - atm you can only create them but if down the road we allow users to
    //update or remove them, they should be sorted
    dayCrewLogicModifiers.sort(
      (a: ICrewLogicModifier, b: ICrewLogicModifier) =>
        (_.isNumber(a.date) ? a.date : a.date?.valueOf()) - (_.isNumber(b.date) ? b.date : b.date?.valueOf())
    );
    crewLogicModifierMap[crewId][dayMonthStr] = dayCrewLogicModifiers;
  });
  return crewLogicModifierMap;
};

export const setCrewPairings = (pairings: IDisplayPairing[], state: ICrewSchedule): ICrewPairingMap => {
  const crewPairings: ICrewPairingMap = { ...state.crewPairings };
  const lastModified = Date.now();

  for (const pairing of pairings) {
    for (const position of pairing.positions) {
      // Has now been modified to support mapping of unassigned pairings. --CC
      const mappingId = !position.crew_id ? unassignedPairingId : position.crew_id;

      if (!mappingId) {
        continue;
      }
      if (!crewPairings[mappingId]) {
        crewPairings[mappingId] = newCrewPairing(lastModified);
      }
      // Work on a NEW array (and a new per-crew object) so subscriptions to
      // `crewPairings[mappingId].pairings` observe a reference change.
      const pairingsArr = [...crewPairings[mappingId].pairings];
      const elemExists = pairingsArr.findIndex((val: IDisplayPairing) => val._id === pairing._id);
      if (elemExists >= 0) {
        pairingsArr.splice(elemExists, 1);
      }
      const index = findIndexToInsertEvent(pairingsArr, pairing, 0, pairingsArr.length - 1);
      pairingsArr.splice(index, 0, pairing);
      crewPairings[mappingId] = {
        ...crewPairings[mappingId],
        pairings: pairingsArr,
        lastModified,
      };
    }
  }
  return crewPairings;
};

const setCrewNonFlyingAssignments = (
  nonFlyingAssignments: ICrewsNonFlyingAssignment[],
  state: ICrewSchedule
): ICrewNonFlyingAssignmentMap => {
  const crewNFAs: ICrewNonFlyingAssignmentMap = {
    ...state.crewNonFlyingAssignments,
  };
  const lastModified = Date.now();

  for (const nonFlyingAssignment of nonFlyingAssignments) {
    for (const crew of nonFlyingAssignment.crew) {
      const crewId = crew._id;
      if (!crewNFAs[crewId]) {
        crewNFAs[crewId] = newCrewNFA(lastModified);
      }

      // Work on a NEW array (and a new per-crew object) so the reference to
      // `crewNonFlyingAssignments[crewId].nonFlyingAssignments` changes whenever
      // its contents do.
      const nfaArr = [...crewNFAs[crewId].nonFlyingAssignments];
      const elemExists = nfaArr.findIndex((val: INonFlyingAssignment) => val._id === nonFlyingAssignment._id);
      //if the NFA already exisit, remove it and check to see where you should add it in
      if (elemExists >= 0) {
        nfaArr.splice(elemExists, 1);
      }
      //add the event to the array at the proper index
      const index = findIndexToInsertEvent(nfaArr, nonFlyingAssignment, 0, nfaArr.length - 1);
      nfaArr.splice(index, 0, nonFlyingAssignment);

      crewNFAs[crewId] = {
        ...crewNFAs[crewId],
        nonFlyingAssignments: nfaArr,
        lastModified,
      };
    }
  }
  return crewNFAs;
};

// a function to find the index of the array to add a new event (based on the event start time)
const findIndexToInsertEvent = (
  arr: (INonFlyingAssignment | IDisplayPairing)[],
  x: INonFlyingAssignment | IDisplayPairing,
  start: number,
  end: number
): number => {
  //  when the value should be added to start of the array
  if (start > end) return 0;

  //if the value should be added to the end of the array
  if (x.start >= arr[arr.length - 1]?.start) return arr.length + 1;
  // Find the middle index
  const mid = Math.floor((start + end) / 2);

  // Compare mid with given key x
  if (arr[mid]?.start <= x.start && arr[mid + 1]?.start > x.start) return mid + 1;

  // If element at mid is greater than x,
  // search in the left half of mid
  if (arr[mid]?.start > x.start) return findIndexToInsertEvent(arr, x, start, mid - 1);
  // If element at mid is smaller than x,
  // search in the right half of mid
  else return findIndexToInsertEvent(arr, x, mid + 1, end);
};

// a function to find the index of the array to add a new event (based on the event start time)
const findIndexToInsertLogicModifier = (
  arr: ICrewLogicModifier[],
  x: ICrewLogicModifier,
  start: number,
  end: number
): number => {
  //  when the value should be added to start of the array
  if (start > end) return 0;

  //if the value should be added to the end of the array
  if (x.date >= arr[arr.length - 1]?.date) return arr.length + 1;
  // Find the middle index
  const mid = Math.floor((start + end) / 2);

  // Compare mid with given key x
  if (arr[mid]?.date <= x.date && arr[mid + 1]?.date > x.date) return mid + 1;

  // If element at mid is greater than x,
  // search in the left half of mid
  if (arr[mid]?.date > x.date) return findIndexToInsertLogicModifier(arr, x, start, mid - 1);
  // If element at mid is smaller than x,
  // search in the right half of mid
  else return findIndexToInsertLogicModifier(arr, x, mid + 1, end);
};

export interface ICrewEvent {
  event: IDisplayPairing | IDisplayBidPairing | ICrewsNonFlyingAssignment | IDisplayBidNFA;
  crewId: string;
  isPlanned: boolean;
}

export interface ISelectedCrewDay {
  timezone: string;
  selectedTimes: number;
  crewId: string;
}

export interface ISelectedCrewDays {
  timezone: string;
  selectedTimes: number[];
  crewId: string;
}

export const isIDisplayPairing = (a: any): a is IDisplayPairing | IDisplayBidPairing => {
  return "positions" in a && "eventGroups" in a && "start" in a && "end" in a;
};

export const isISearchPairing = (a: any): a is ISearchPairing => {
  return "positions" in a && "start" in a && "end" in a;
};

export const isINonFlyingAssignment = (a: any): a is INonFlyingAssignment => {
  return "start" in a && "end" in a && "locationICAO" in a && "assignmentType" in a;
};

export const isINonFlyingPairingAssignment = (a: any): a is INonFlyingPairingAssignment => {
  return "location" in a && "isFullDay" in a && "start" in a && "end" in a;
};

//just for the context of what is on crew schedule timeline

export interface ICrewScheduleBaseEvent {
  _id: any;
  start: number;
  end: number;
  locationICAO: string; //nfa location or the pairing base
  isDuty: boolean; //always true for pairings
  eventType: "pairing" | "nonflyingassignment";
}
export interface ICrewScheduleEvent {
  events: ICrewScheduleBaseEvent[];
  lastModified: number;
}

const updateSelectedCrewEvents = (
  crewPairings: ICrewPairingMap,
  crewNFAs: ICrewNonFlyingAssignmentMap,
  crewIds: string[],
  currentEvents: ICrewEvent[],
  newEvents: IHoveredEvent[],
  shiftKey: boolean, // if shift is passed in as true, select range of events.
  ctrlKey: boolean,
  update?: boolean
): ICrewEvent[] => {
  const itemIndex: number | false =
    newEvents?.length === 1 &&
    currentEvents?.findIndex((event: ICrewEvent) => event.event._id === newEvents?.[0].event._id);
  if (update) {
    // If selected event exists in current selection, remove.
    return [...newEvents];
  }
  if (ctrlKey && !shiftKey) {
    if (itemIndex === -1) {
      // Append If selected events are matching type, otherwise create new selection array.
      const appendedEvents =
        currentEvents.length > 0 &&
        isIDisplayPairing(newEvents[0]?.event) === isIDisplayPairing(currentEvents[0]?.event)
          ? [...currentEvents, ...newEvents]
          : newEvents;
      return [...appendedEvents];
    } else {
      if (currentEvents?.length > 1) {
        // If selected event exists in current selection, remove.
        currentEvents.splice(itemIndex, 1);
        return [...currentEvents];
      }
      return []; // clear selection
    }
  } else if (!ctrlKey && !shiftKey) {
    // If there's only one event selected and it matches the current one, clear it.
    if (itemIndex === 0 && currentEvents?.length === 1) {
      return []; // clear selection
    }
    return [...newEvents];
  } else if (!ctrlKey && shiftKey) {
    // Create array of all unique crew ids found in newly selected crew events.
    const currentCrewIds = Array.from(new Set([...currentEvents.map((event) => event.crewId)]));
    // if shift key was held down and new selected events match crew of current selected, select range between both events.
    const selectRangeOfEvents =
      crewIds?.length === 1 && currentCrewIds?.length === 1 && crewIds[0] === currentCrewIds[0];
    const newSelections = [...newEvents, ...currentEvents];

    // Only do shift operations on matching types of events (pairings and non flying assignments)
    const allTypePairing =
      newSelections?.filter((event) => !!(event?.event as IDisplayPairing)?.shortId)?.length === newSelections?.length;
    const allTypeNFA =
      newSelections?.filter((event) => !!(event?.event as ICrewsNonFlyingAssignment)?.assignmentType?.title)?.length ===
      newSelections?.length;

    if (!selectRangeOfEvents || (!allTypePairing && !allTypeNFA)) {
      // if conditions of currently selected events dont match news ones, return newly selected events.
      return [...newEvents];
    }

    // find earliest start and latest end in current selections.
    const earliestStart = newSelections.reduce((eventA: ICrewEvent, eventB: ICrewEvent) =>
      new Date(eventA.event.start) < new Date(eventB.event.start) ? eventA : eventB
    );
    const latestEnd = newSelections.reduce((eventA: ICrewEvent, eventB: ICrewEvent) =>
      new Date(eventA.event.end) > new Date(eventB.event.end) ? eventA : eventB
    );

    const crewEvents = allTypePairing
      ? crewPairings?.[crewIds[0]]?.pairings
      : crewNFAs?.[crewIds[0]]?.nonFlyingAssignments;
    // Select all pairing within unique aircraft range.
    const selectedPairingEvents = crewEvents
      //@ts-ignore
      .filter(
        (e: IDisplayPairing | ICrewsNonFlyingAssignment) =>
          e.start >= earliestStart.event.start && e.start <= latestEnd.event.start
      )
      .map((item: IDisplayPairing | ICrewsNonFlyingAssignment) => ({
        event: item,
        crewId: crewIds[0],
        isPlanned: false,
      }));

    return [...selectedPairingEvents];
  }
  return [...newEvents];
};

export const newCrewPlannedEvent = (lastModified: number): ICrewMemberPlannedEvents => {
  return {
    plannedEvents: [],
    lastModified,
  };
};

const updateCrewPlannedEventMap = (
  crewPlannedEvents: ICrewSchedEventMap,
  plannedEvents: CrewSchedEvent[]
): ICrewSchedEventMap => {
  const updatedCrewPlannedEvents = { ...crewPlannedEvents };
  plannedEvents.sort((a: CrewSchedEvent, b: CrewSchedEvent) => {
    return a?.start?.valueOf() - b?.start?.valueOf();
  });
  const lastModified = Date.now();
  // Track which crews we've already cloned this call so each per-crew object and
  // its plannedEvents array is rebuilt exactly once (new reference for subscribers,
  // no in-place mutation of previous state).
  const clonedCrewIds: { [crewId: string]: boolean } = {};
  for (let i = 0; i < plannedEvents.length; i++) {
    const currProcessedEvent = plannedEvents[i];
    const crewMembers = isIDisplayPairing(currProcessedEvent) ? currProcessedEvent.positions : currProcessedEvent?.crew;
    if (!crewMembers) {
      continue;
    }
    for (const crewMember of crewMembers) {
      const crewId = isIPairingPosition(crewMember) ? crewMember.crew_id : crewMember._id;

      if (!clonedCrewIds[crewId]) {
        const existing = updatedCrewPlannedEvents[crewId];
        updatedCrewPlannedEvents[crewId] = existing
          ? { ...existing, plannedEvents: [...existing.plannedEvents], lastModified }
          : newCrewPlannedEvent(lastModified);
        clonedCrewIds[crewId] = true;
      }

      const crewPlannedEventGroups = updatedCrewPlannedEvents[crewId].plannedEvents;
      const foundOverlapIndex = findOverlappingIndex(crewPlannedEventGroups, currProcessedEvent);
      if (foundOverlapIndex >= 0) {
        crewPlannedEventGroups[foundOverlapIndex] = [...crewPlannedEventGroups[foundOverlapIndex], currProcessedEvent];
      } else {
        //find index to add planned event
        const index = findIndexForPlannedEvents(
          crewPlannedEventGroups,
          currProcessedEvent,
          0,
          crewPlannedEventGroups.length - 1
        );
        crewPlannedEventGroups.splice(index, 0, [currProcessedEvent]);
      }
    }
  }
  return updatedCrewPlannedEvents;
};

export const findOverlappingIndex = (plannedEvents: CrewSchedEvent[][], event: CrewSchedEvent): number => {
  return plannedEvents.findIndex((overlappingEvents: CrewSchedEvent[]) => {
    return (
      overlappingEvents.findIndex((plannedEvent: CrewSchedEvent) => {
        return plannedEvent && event && plannedEvent?.start <= event?.end && event?.start < plannedEvent?.end;
      }) >= 0
    );
  });
};

const findIndexForPlannedEvents = (
  arr: CrewSchedEvent[][],
  x: INonFlyingAssignment | IDisplayPairing,
  start: number,
  end: number
): number => {
  //  when the value should be added to start of the array
  if (start > end) return 0;

  //if the value should be added to the end of the array
  if (x.start >= arr[arr.length - 1]?.[0]?.start) return arr.length + 1;
  // Find the middle index
  const mid = Math.floor((start + end) / 2);

  // Compare mid with given key x
  if (arr[mid]?.[0].start <= x.start && arr[mid + 1]?.[0]?.start > x.start) return mid + 1;

  // If element at mid is greater than x,
  // search in the left half of mid
  if (arr[mid]?.[0]?.start > x.start) return findIndexForPlannedEvents(arr, x, start, mid - 1);
  // If element at mid is smaller than x,
  // search in the right half of mid
  else return findIndexForPlannedEvents(arr, x, mid + 1, end);
};

//so anytime you hit this, you have ALL the qualifications coming in
// always overwritting - never appending
export const updateCrewQualifications = (
  currentCrewQualifications: ICrewQualifications,
  qualifications: IQualificationCheckResult[],
  crewIds?: string[]
): ICrewQualifications => {
  if (crewIds?.length) {
    return replaceCrewQualifications(currentCrewQualifications, qualifications, crewIds);
  }

  const crewQualifications: { [key: string]: any } = {}; //cloneDeep(currentCrewQualifications);
  for (const qualification of qualifications) {
    const { checkId, crewId } = qualification;
    const curType = crewQualifications?.[crewId]?.qualifications?.[checkId] ?? [];
    crewQualifications[crewId] = {
      qualifications: crewQualifications[crewId]?.qualifications
        ? { ...crewQualifications[crewId]?.qualifications, [checkId]: [...curType, qualification] }
        : { [checkId]: [...curType, qualification] }, //this should be able to hold an ARRAY of qulifications
      createdAt: Date.now(),
    };
  }
  return crewQualifications;
};

/**
 * Replace crew qualifications for the specified crewIds with the incoming qualifications.
 * This is used when we want to replace all qualifications for a crew or set of crews, such as when we fetch qualifications for a specific crew or group of crews.
 * It first removes all existing qualifications for the specified crewIds and then adds the incoming qualifications.
 * @param currentCrewQualifications
 * @param qualifications
 * @param crewIds
 * @returns
 */
export const replaceCrewQualifications = (
  currentCrewQualifications: ICrewQualifications | null,
  qualifications: IQualificationCheckResult[],
  crewIds: string[]
): ICrewQualifications => {
  const crewQualifications: ICrewQualifications = { ...(currentCrewQualifications ?? {}) };

  // Clear existing entries for all queried crew members to avoid duplicate accumulation on re-fetch
  for (const crewId of crewIds) {
    delete crewQualifications[crewId];
  }

  // Build up qualifications from incoming data only
  const now = Date.now();
  for (const qualification of qualifications) {
    const { checkId, crewId } = qualification;
    const curType = crewQualifications?.[crewId]?.qualifications?.[checkId] ?? [];
    crewQualifications[crewId] = {
      qualifications: crewQualifications[crewId]?.qualifications
        ? { ...crewQualifications[crewId].qualifications, [checkId]: [...curType, qualification] }
        : { [checkId]: [...curType, qualification] },
      createdAt: now,
    };
  }

  return crewQualifications;
};

//a free for all function that sets any property in the window properties
//if you pass in a windowId, it just sets it in that window, if no window id, sets in all open crew sched windows
const setWindowProperty = (
  propertyKey: string,
  propertyValue: any,
  currentProperties: ICrewScheduleWindowProperties,
  windowId?: string //would you ever want to take in an array of window ids ie set 2/4 windows
): ICrewScheduleWindowProperties => {
  if (!propertyKey || propertyValue === undefined) return currentProperties;
  if (windowId) {
    return {
      ...currentProperties,
      [windowId]: { ...currentProperties[windowId], [propertyKey]: propertyValue },
    };
  } else {
    //get all the windowIds and set the property
    const windowIds = Object.keys(currentProperties);
    windowIds.forEach((windowId) => {
      currentProperties[windowId] = { ...currentProperties[windowId], [propertyKey]: propertyValue };
    });
    return currentProperties;
  }
};
